import { Content } from "@/ui";
import { getRoute } from "@/functions/getRoute";
import unslug from "unslug";
import { simple_pages_helper } from "@/helpers/simple_pages_helper";
import { createTitle } from "@/functions/createTitle";
import { consultant_helper } from "@/helpers/consultant_helper";
import { jobs_helper } from "@/helpers/jobs_helper";
import SeoHead from "@/components/SeoHead";
import { statistics_helper } from "@/helpers/statistics_helper";
import { getColorVariant } from "@/functions/getColorVariant";

export default function Page({ content, meta }) {
  return (
    <>
      <SeoHead
        page_name={`${meta.title} · Omega`}
        canonical_link={`https://www.omegaresource.co.uk/${meta.url_slug}/`}
      />
      <Content items={content} />
    </>
  );
}

function generateContentSection(contents, section) {

  if (!contents) {
    return [{}];
  }

  let list = contents.map(content => {
    if (!(!content.location || parseInt(content.location) === parseInt(section))) {
      return {}
    } else {
      return {
        component: "ContentSection",
        props: {
          title: content.title || "",
          highlight: content.highlight || "",
          description: content.description || "",
          image: content.image || "",
          alignment: content.alignment || "",
          button1_link: content.button1_link || "",
          button1_text: content.button1_text || "",
          button2_link: content.button2_link || "",
          button2_text: content.button2_text || "",
          color_variant: getColorVariant(content),
          alt_text: content.alt_text || "",
        }
      }
    }
  });

  list = list.filter(item => !!item.component);

  return list;
}

export async function getStaticProps({ params: { url_slugs } }) {
  const pages = url_slugs.map((url_slug, k) => ({
    url_slug,
    label: unslug(url_slug),
    href: getRoute("dynamic", { url_slugs: url_slugs.slice(0, k + 1) }),
  }));
  let [_page, prevPage] = [...pages].reverse();
  const page = simple_pages_helper.find(_page.url_slug);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      meta: {
        title: createTitle(...pages.map((i) => i.label).reverse()),
        url_slug: _page.url_slug,
      },
      content: [
        {
          component: "Hero",
          skipFade: true,
          props: {
            logo: "",
            title: page.title,
            subtitle: page.subtitle,
            backgrounds: page.background,
            color_variant: getColorVariant(page),
          },
        },
        page.description ? {
          component: "DescriptionSection",
          props: {
            description: page.description,
          },
        } : {},
        ...(generateContentSection(page.content, 1)),
        ...(page.sectors || []).map(sector => {
          return  {
            component: "Sectors",
            props: {
              title: sector.title || "",
              highlight: sector.highlight || "",
              color_variant: getColorVariant(sector),
            },
          }
        }),
        ...(page.service_section || []).map(item => {
          return  {
            component: "Services",
            props: {
              title: item.title || "",
              highlight: item.highlight || "",
              color_variant: getColorVariant(item),
            },
          }
        }),
        ...page.stats.map(item => {
          return  {
            component: "Stats",
            props: {
              title: item.title || "",
              highlight: item.highlight || "",
              color_variant: getColorVariant(item),
              items: statistics_helper.fetch(),
            },
          }
        }),
        ...(page.offers_section || []).map(offers => {
          return  {
            component: "Offers",
            props: {
              title: offers.title || "",
              color_variant: getColorVariant(offers),
            },
          }
        }),
        ...(generateContentSection(page.content, 2)),
        ...(page.specialisms_section || []).map(item => {
          return  {
            component: "Specialisms",
            props: {
              sector: page.sector || "",
              parentId: page.id,
              title: item.title || "",
              highlight: item.highlight || "",
              color_variant: getColorVariant(item),
            },
          }
        }),
        ...(page.team_section || []).map(offers => {
          return  {
            component: "OurTeam",
            props: {
              title: offers.title || "",
              highlight: offers.highlight || "",
              color_variant: getColorVariant(offers),
              items: consultant_helper.fetch({sector: page.sector}),
              sector: page.sector || "",
            },
          }
        }),
        ...(page.jobs_section || []).map(item => {
          return  {
            component: "Jobs",
            props: {
              title: item.title || "",
              highlight: item.highlight || "",
              color_variant: getColorVariant(item),
              items: jobs_helper.fetch({sector: page.sector}),
            },
          }
        }),
        ...(page.branches_section || []).map(branch => {
          return  {
            component: "Branches",
            props: {
              title: branch.title || "",
            },
          }
        }),
        ...(page.form || []).map(form => {
          return  {
            component: "FormSection",
            props: {
              title: form.title || "",
              formId: form.form_id || "",
              redirect_url: form.redirect_url || "",
            },
          }
        }),
        page.shape_future_section &&
        {
          component: "JoinUs",
          props: {},
        },
        ...(page.certificate_section || []).map(certificate => {
          return  {
            component: "Certificates",
            props: {
              title: certificate.title || "",
              highlight: certificate.highlight || "",
              color_variant: getColorVariant(certificate),
            },
          }
        }),
      ],
    },
  };
}

export async function getStaticPaths() {
  return {
    paths: simple_pages_helper.toNestedPaths({
      exclude: [
        "8b411c69-ef99-4600-828d-5daeb7d625a5", // insights
        "1a27181c-c527-4de5-b7a4-0a52010702d9", // home
        "6222bf4e-b362-44c4-bbdb-4e8bdb6bb059", // meet our team
        "56a4fdab-4a96-4544-b6b0-7e5c3a30963d", // jobs
      ],
    }),
    fallback: false,
  };
}
